import React, { useState } from 'react';

const Analytics = () => {
  const [timeRange, setTimeRange] = useState('24h');

  const metrics = {
    totalMessages: 1247,
    uniqueUsers: 43,
    avgResponseTime: '2.3s',
    successRate: '98.5%',
    activeChannels: 12,
    commandsUsed: 567
  };

  const topCommands = [
    { command: '/ask-claude', count: 234, percentage: 41 },
    { command: '/market-status', count: 156, percentage: 28 },
    { command: '/place-bet', count: 98, percentage: 17 },
    { command: '/voice-call', count: 45, percentage: 8 },
    { command: '/help', count: 34, percentage: 6 }
  ];

  const channelActivity = [
    { channel: '#general', messages: 342, active: true },
    { channel: '#trading', messages: 289, active: true },
    { channel: '#dev-team', messages: 201, active: true },
    { channel: '#random', messages: 156, active: false },
    { channel: '#support', messages: 134, active: true }
  ];

  const hourlyActivity = Array.from({ length: 24 }, (_, i) => ({
    hour: i,
    messages: Math.floor(Math.random() * 100) + 10
  }));

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex gap-2">
        {['1h', '24h', '7d', '30d'].map((range) => (
          <button
            key={range}
            onClick={() => setTimeRange(range)}
            className={`px-4 py-2 rounded-lg transition-colors ${
              timeRange === range
                ? 'bg-green-600 text-black'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            {range}
          </button>
        ))}
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-2xl font-bold text-green-400">{metrics.totalMessages}</div>
          <div className="text-xs text-gray-400">Total Messages</div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-2xl font-bold text-yellow-400">{metrics.uniqueUsers}</div>
          <div className="text-xs text-gray-400">Unique Users</div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-2xl font-bold text-blue-400">{metrics.avgResponseTime}</div>
          <div className="text-xs text-gray-400">Avg Response</div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-2xl font-bold text-green-400">{metrics.successRate}</div>
          <div className="text-xs text-gray-400">Success Rate</div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-2xl font-bold text-purple-400">{metrics.activeChannels}</div>
          <div className="text-xs text-gray-400">Active Channels</div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-2xl font-bold text-pink-400">{metrics.commandsUsed}</div>
          <div className="text-xs text-gray-400">Commands Used</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Commands */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-xl font-bold text-green-400 mb-4">Top Commands</h3>
          <div className="space-y-3">
            {topCommands.map((cmd) => (
              <div key={cmd.command} className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-sm text-yellow-400">{cmd.command}</span>
                    <span className="text-xs text-gray-400">{cmd.count}</span>
                  </div>
                  <div className="w-full bg-black rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${cmd.percentage}%` }}
                    />
                  </div>
                </div>
                <span className="text-xs text-gray-500">{cmd.percentage}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Channel Activity */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-xl font-bold text-green-400 mb-4">Channel Activity</h3>
          <div className="space-y-2">
            {channelActivity.map((channel) => (
              <div
                key={channel.channel}
                className="flex items-center justify-between bg-black p-3 rounded"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${
                    channel.active ? 'bg-green-500' : 'bg-gray-500'
                  }`} />
                  <span className="text-sm">{channel.channel}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-400">{channel.messages} msgs</span>
                  <span className={`text-xs px-2 py-1 rounded ${
                    channel.active
                      ? 'bg-green-900 text-green-300'
                      : 'bg-gray-700 text-gray-400'
                  }`}>
                    {channel.active ? 'Active' : 'Idle'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Hourly Activity Chart */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-green-400 mb-4">24-Hour Activity</h3>
        <div className="flex items-end gap-1 h-32">
          {hourlyActivity.map((hour) => (
            <div
              key={hour.hour}
              className="flex-1 bg-green-500 rounded-t hover:bg-green-400 transition-colors"
              style={{ height: `${(hour.messages / 110) * 100}%` }}
              title={`${hour.hour}:00 - ${hour.messages} messages`}
            />
          ))}
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-2">
          <span>00:00</span>
          <span>06:00</span>
          <span>12:00</span>
          <span>18:00</span>
          <span>23:00</span>
        </div>
      </div>

      {/* Performance Insights */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-green-400 mb-4">Performance Insights</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-black p-4 rounded">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Response Time Trend</span>
              <span className="text-xs text-green-400">↓ 15%</span>
            </div>
            <p className="text-xs text-gray-500">
              Average response time improved by 15% compared to last period
            </p>
          </div>
          <div className="bg-black p-4 rounded">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">User Engagement</span>
              <span className="text-xs text-green-400">↑ 23%</span>
            </div>
            <p className="text-xs text-gray-500">
              23% more unique users interacting with Claude this period
            </p>
          </div>
          <div className="bg-black p-4 rounded">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Error Rate</span>
              <span className="text-xs text-yellow-400">→ 0.5%</span>
            </div>
            <p className="text-xs text-gray-500">
              Error rate stable at 0.5%, well within acceptable limits
            </p>
          </div>
          <div className="bg-black p-4 rounded">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Command Usage</span>
              <span className="text-xs text-green-400">↑ 34%</span>
            </div>
            <p className="text-xs text-gray-500">
              34% increase in slash command usage showing strong adoption
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;